import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { SavedSearchFilter } from '@shared/models/search/saved-search-filters.model';
import { SharedModule } from '@shared/shared.module';
import { EMPTY, Observable, Subject, switchMap, take } from 'rxjs';
import { UserPreferencesService } from '@services/user/user-preferences.service';
import { CoupleIdLite } from '@shared/models/generic/id/couple-id-lite.model';
import { NotificationService } from '@services/notification.service';
import { SaveFiltersDialogComponent } from '@shared/components/filters-dialog/save-filter-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { catchError, filter, tap } from 'rxjs/operators';
import { TextPopinComponent } from '@shared/components/text-popin/text-popin.component';

@Component({
  selector: 'app-saved-search-filters',
  templateUrl: './saved-search-filters.component.html',
  styleUrls: ['./saved-search-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule],
})
export class SavedSearchFiltersComponent implements OnInit, OnDestroy {
  @Input() title = 'FACETS.SAVED';
  @Input() scope = 'recipe';
  readonly savedFilters$: Observable<SavedSearchFilter[]> = this.userPreferencesService.userFilters$;
  private readonly notificationService = inject(NotificationService);

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly userPreferencesService: UserPreferencesService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.userPreferencesService.getSavedSearchFilters(this.scope).subscribe();
  }

  onSelect(selectedSearchFilter: SavedSearchFilter): void {
    this.userPreferencesService.select(selectedSearchFilter);
  }

  onEdit(searchFilter: SavedSearchFilter): void {
    this.dialog.open(SaveFiltersDialogComponent, {
      data: {
        scope: this.scope,
        identifier: searchFilter.identifier,
        title: searchFilter.title,
      },
    });
  }

  onDelete(identifier: CoupleIdLite): void {
    this.dialog
      .open(TextPopinComponent, {
        data: { title: 'FILTERS.CONFIRM_DELETE_TITLE', text: 'FILTERS.CONFIRM_DELETE_MSG' },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((confirmed: boolean) => confirmed),
        switchMap(() =>
          this.userPreferencesService.removeSavedFilters(identifier).pipe(
            tap(() => this.notificationService.sendSuccess('FILTERS.DELETED')),
            catchError(() => {
              this.notificationService.sendError('FILTERS.ERROR_DELETE');
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }

  trackById(_: number, item: SavedSearchFilter): string {
    return item.identifier.functionalId;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.userPreferencesService.select(null);
  }
}
